import { configureApiRuntime } from '@ddone/api-client';
import type { TimerState } from '@ddone/shared-types';

export interface DesktopTimerSnapshot {
  status: 'STOPPED' | 'RUNNING' | 'PAUSED';
  startedAt?: number;
  elapsedSeconds: number;
  description?: string;
  serverTimerId?: string;
}

export interface DesktopBootstrap {
  version: string;
  operatingSystem: string;
  architecture: string;
  selectedServer?: string;
  recentServers: Array<{ url: string; lastUsedAt: number }>;
  updaterEnabled: boolean;
  timer: DesktopTimerSnapshot;
}

export interface DesktopUpdateStatus {
  enabled: boolean;
  available: boolean;
  currentVersion: string;
  version?: string;
  notes?: string;
  publishedAt?: string;
}

interface AccessTokenView {
  accessToken: string;
  tokenType: string;
  expiresAt: number;
  scope?: string;
}

interface DesktopCommandError {
  code?: string;
  message?: string;
}

let selectedServer: string | undefined;
let token: AccessTokenView | undefined;

export function isTauriDesktop(): boolean {
  return typeof window !== 'undefined' && '__TAURI_INTERNALS__' in window;
}

export async function desktopBootstrap(): Promise<DesktopBootstrap> {
  const bootstrap = await invoke<DesktopBootstrap>('desktop_bootstrap');
  selectedServer = bootstrap.selectedServer;
  if (selectedServer) configureDesktopApi(selectedServer);
  return bootstrap;
}

export async function selectDesktopServer(serverUrl: string): Promise<DesktopBootstrap> {
  selectedServer = await invoke<string>('configure_server', { serverUrl });
  token = undefined;
  configureDesktopApi(selectedServer);
  return desktopBootstrap();
}

export async function beginDesktopLogin(): Promise<void> {
  if (!selectedServer) throw new Error('Seleziona prima il server DDone.');
  const issuer = import.meta.env.VITE_DDONE_DESKTOP_OIDC_ISSUER ?? `${selectedServer}/realms/ddone`;
  await invoke('begin_oauth', {
    request: {
      serverUrl: selectedServer,
      authorizationEndpoint: `${issuer}/protocol/openid-connect/auth`,
      tokenEndpoint: `${issuer}/protocol/openid-connect/token`,
      clientId: 'ddone-desktop',
      scopes: ['openid', 'profile', 'email', 'offline_access']
    }
  });
}

export async function listenForDesktopOAuth(
  onAuthenticated: () => void,
  onError: (error: unknown) => void
): Promise<() => void> {
  return listen<string>('desktop-oauth-callback', async (callbackUrl) => {
    try {
      token = await invoke<AccessTokenView>('complete_oauth_callback', { callbackUrl });
      onAuthenticated();
    } catch (error) {
      onError(error);
    }
  });
}

export async function listenForDesktopEvent<T>(
  event: string,
  listener: (payload: T) => void | Promise<void>
): Promise<() => void> {
  return listen<T>(event, listener);
}

export async function syncDesktopTimer(timer: TimerState | null): Promise<void> {
  if (!isTauriDesktop()) return;
  const nowSeconds = Math.floor(Date.now() / 1000);
  await invoke('timer_sync', {
    timer: timer
      ? {
          status: timer.status,
          startedAt:
            timer.status === 'RUNNING'
              ? Math.floor(new Date(timer.startedAt).getTime() / 1000)
              : undefined,
          accumulatedSeconds:
            timer.status === 'RUNNING'
              ? Math.max(0, timer.accumulatedSeconds)
              : Math.max(
                  0,
                  timer.accumulatedSeconds ||
                    nowSeconds - Math.floor(new Date(timer.startedAt).getTime() / 1000)
                ),
          description: timer.description,
          serverTimerId: timer.id
        }
      : {
          status: 'STOPPED',
          accumulatedSeconds: 0
        }
  });
}

export function showDesktopNotification(title: string, body: string): Promise<void> {
  return invoke('show_native_notification', { title, body });
}

export function checkDesktopUpdate(): Promise<DesktopUpdateStatus> {
  return invoke<DesktopUpdateStatus>('check_for_update');
}

export function installDesktopUpdate(): Promise<void> {
  return invoke('install_update');
}

export function setDesktopUpdaterEnabled(enabled: boolean): Promise<void> {
  return invoke('set_updater_enabled', { enabled });
}

async function desktopAccessToken(): Promise<string | undefined> {
  if (!selectedServer) return undefined;
  const now = Math.floor(Date.now() / 1000);
  if (token && token.expiresAt > now + 30) return token.accessToken;
  try {
    token = await invoke<AccessTokenView>('access_token', { serverUrl: selectedServer });
  } catch (error) {
    if (errorCode(error) === 'access-token-expired') {
      token = await invoke<AccessTokenView>('refresh_access_token', {
        serverUrl: selectedServer
      });
    } else {
      return undefined;
    }
  }
  return token.accessToken;
}

function configureDesktopApi(serverUrl: string): void {
  configureApiRuntime({
    baseUrl: `${serverUrl.replace(/\/$/, '')}/api/v1`,
    accessToken: desktopAccessToken,
    logout: async () => {
      const issuer = import.meta.env.VITE_DDONE_DESKTOP_OIDC_ISSUER ?? `${serverUrl}/realms/ddone`;
      await invoke('logout', {
        serverUrl,
        revocationEndpoint: `${issuer}/protocol/openid-connect/revoke`
      });
      token = undefined;
    }
  });
}

async function invoke<T = void>(command: string, args?: Record<string, unknown>): Promise<T> {
  const { invoke: tauriInvoke } = await import('@tauri-apps/api/core');
  return tauriInvoke<T>(command, args);
}

async function listen<T>(event: string, listener: (payload: T) => void | Promise<void>) {
  const { listen: tauriListen } = await import('@tauri-apps/api/event');
  return tauriListen<T>(event, ({ payload }) => void listener(payload));
}

function errorCode(error: unknown): string | undefined {
  if (typeof error === 'object' && error !== null && 'code' in error) {
    return (error as DesktopCommandError).code;
  }
  return undefined;
}
